import type { CustomFieldValues } from "@forgepoint/shared";

/**
 * Normalizes custom field values into a display-string map.
 * @param fields - The raw custom field values, or null/undefined when absent
 * @returns A map of field name to display string, skipping null values
 */
export function normalizeCustomFields(
  fields: CustomFieldValues | null | undefined,
): Record<string, string> {
  if (!fields) {
    return {};
  }
  return Object.entries(fields).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (value === null || value === undefined) {
        return acc;
      }
      acc[key] = Array.isArray(value) ? value.join(", ") : String(value);
      return acc;
    },
    {},
  );
}

/**
 * Looks up a normalized custom field value by case-insensitive, trimmed name.
 * @param fields - The normalized custom field map
 * @param fieldName - The field name to look up
 * @returns The matching value, or null when no field matches
 */
export function lookupCustomField(
  fields: Record<string, string>,
  fieldName: string,
): string | null {
  const target = fieldName.trim().toLowerCase();
  const key = Object.keys(fields).find(
    (candidate) => candidate.trim().toLowerCase() === target,
  );
  return key !== undefined ? fields[key] : null;
}
